/**
 * LoginPage — страница входа через invite-код или LDAP
 * Показывается при 401/403 ответе от API или при переходе на /login
 */
import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { api } from '../utils/api'

export default function LoginPage() {
  const [activeTab, setActiveTab] = useState<'invite' | 'ldap'>('invite')
  const [code, setCode] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const login = useAuthStore(s => s.login)

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      let resp
      if (activeTab === 'invite') {
        if (!code.trim()) {
          setLoading(false)
          return
        }
        resp = await api.post('/api/auth/login', { code: code.trim() })
      } else {
        if (!username.trim() || !password) {
          setLoading(false)
          return
        }
        resp = await api.post('/api/auth/login', {
          username: username.trim(),
          password,
        })
      }
      
      login(resp.data.role, resp.data.username || username || 'user', resp.data.access_token)
      navigate('/', { replace: true })
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Неверный код доступа или учетные данные'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [activeTab, code, username, password, navigate, login])

  return (
    <div id="auth-screen" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0b0e14 0%, #0d1424 50%, #0b0e14 100%)',
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        padding: '0 20px',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 20px',
            background: 'rgba(6, 182, 212, 0.08)',
            border: '1px solid rgba(6, 182, 212, 0.2)',
            borderRadius: '12px',
            marginBottom: '16px',
          }}>
            <span style={{ color: '#06b6d4', fontWeight: 700, fontSize: '16px', letterSpacing: '0.1em' }}>DC</span>
            <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '14px' }}>CABLE LINK</span>
          </div>
          <p style={{ color: '#475569', fontSize: '13px', margin: 0 }}>
            Система управления инфраструктурой ЦОД
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.95)',
          border: '1px solid rgba(51, 65, 85, 0.6)',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
        }}>
          <h1 style={{
            color: '#e2e8f0',
            fontSize: '20px',
            fontWeight: 700,
            margin: '0 0 8px 0',
            letterSpacing: '-0.01em',
          }}>
            Вход в систему
          </h1>
          <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 24px 0' }}>
            Выберете способ авторизации
          </p>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            marginBottom: '24px',
            borderBottom: '1px solid rgba(51, 65, 85, 0.4)',
          }}>
            <button
              type="button"
              className="auth-tab"
              onClick={() => {
                setActiveTab('invite')
                setError(null)
              }}
              style={{
                flex: 1,
                padding: '10px 0',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'invite' ? '2px solid #06b6d4' : '2px solid transparent',
                color: activeTab === 'invite' ? '#e2e8f0' : '#475569',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Код доступа
            </button>
            <button
              type="button"
              className="auth-tab"
              onClick={() => {
                setActiveTab('ldap')
                setError(null)
              }}
              style={{
                flex: 1,
                padding: '10px 0',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'ldap' ? '2px solid #06b6d4' : '2px solid transparent',
                color: activeTab === 'ldap' ? '#e2e8f0' : '#475569',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              LDAP
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {activeTab === 'invite' ? (
              <div style={{ marginBottom: '20px' }}>
                <label
                  htmlFor="invite-code"
                  style={{
                    display: 'block',
                    color: '#94a3b8',
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                  }}>
                  Код доступа
                </label>
                <input
                  id="invite-code"
                  name="invite_code"
                  type="text"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  placeholder="xxxx-xxxx-xxxx"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: `1px solid ${error ? '#ef4444' : 'rgba(51, 65, 85, 0.8)'}`,
                    borderRadius: '8px',
                    color: '#e2e8f0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    letterSpacing: '0.05em',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s',
                  }}
                />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label
                    htmlFor="ldap-username"
                    style={{
                      display: 'block',
                      color: '#94a3b8',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      marginBottom: '8px',
                    }}>
                    Имя пользователя
                  </label>
                  <input
                    id="ldap-username"
                    name="username"
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="ivanov.i"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(30, 41, 59, 0.8)',
                      border: `1px solid ${error ? '#ef4444' : 'rgba(51, 65, 85, 0.8)'}`,
                      borderRadius: '8px',
                      color: '#e2e8f0',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="ldap-password"
                    style={{
                      display: 'block',
                      color: '#94a3b8',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      marginBottom: '8px',
                    }}>
                    Пароль
                  </label>
                  <input
                    id="ldap-password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(30, 41, 59, 0.8)',
                      border: `1px solid ${error ? '#ef4444' : 'rgba(51, 65, 85, 0.8)'}`,
                      borderRadius: '8px',
                      color: '#e2e8f0',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
            )}

            {error && (
              <p style={{ color: '#f87171', fontSize: '12px', margin: '0 0 16px 0' }}>
                ⚠ {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || (activeTab === 'invite' ? !code.trim() : (!username.trim() || !password))}
              style={{
                width: '100%',
                padding: '12px',
                background: loading || (activeTab === 'invite' ? !code.trim() : (!username.trim() || !password))
                  ? 'rgba(6, 182, 212, 0.3)'
                  : 'linear-gradient(135deg, #0891b2, #06b6d4)',
                border: 'none',
                borderRadius: '8px',
                color: loading || (activeTab === 'invite' ? !code.trim() : (!username.trim() || !password)) ? '#64748b' : '#fff',
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: loading || (activeTab === 'invite' ? !code.trim() : (!username.trim() || !password)) ? 'not-allowed' : 'pointer',
                letterSpacing: '0.05em',
                transition: 'all 0.2s',
              }}
            >
              {loading ? 'Вход...' : 'Войти →'}
            </button>
          </form>

          <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid rgba(51, 65, 85, 0.4)',
          }}>
            <p style={{ color: '#475569', fontSize: '12px', margin: 0, textAlign: 'center' }}>
              Также доступен вход через Telegram
            </p>
            <a
              href="/api/auth/telegram"
              style={{
                display: 'block',
                textAlign: 'center',
                marginTop: '12px',
                padding: '10px',
                background: 'rgba(36, 160, 237, 0.1)',
                border: '1px solid rgba(36, 160, 237, 0.2)',
                borderRadius: '8px',
                color: '#38bdf8',
                fontSize: '13px',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'all 0.2s',
              }}
            >
              🔵 Войти через Telegram
            </a>
          </div>
        </div>

        <p style={{ color: '#334155', fontSize: '11px', textAlign: 'center', marginTop: '20px' }}>
          DC Cable Link · DCIM Platform
        </p>
      </div>
    </div>
  )
}
